/**
 * Parse an indexed shapeId of the form `"prefix:N"` and return the numeric
 * index, or `null` when the string doesn't match the expected pattern.
 */
function indexFromShapeId(prefix: string, shapeId: string): number | null {
  const match = new RegExp(`^${prefix}:(\\d+)$`).exec(shapeId);
  if (!match) {
    return null;
  }
  const idx = Number.parseInt(match[1], 10);
  return Number.isNaN(idx) ? null : idx;
}

/** Extract the perimeter index from `"outline:N"`, or `null`. */
export function floorPerimeterIndexFromShapeId(shapeId: string): number | null {
  return indexFromShapeId("outline", shapeId);
}

/** Extract the opening index from `"opening:N"`, or `null`. */
export function floorOpeningIndexFromShapeId(shapeId: string): number | null {
  return indexFromShapeId("opening", shapeId);
}

/**
 * Floor perimeters and openings each have their own independently-numbered stable edge ID
 * namespace, so a bare edge ID (e.g. `"2"`) is ambiguous between rings — the same value can
 * legitimately identify different edges in the perimeter vs. an opening. `EdgeModifier.edgeId`
 * for a FLOOR parent is therefore namespaced by ring + index using these helpers, extending the
 * `outline:N` / `opening:N` shapeId convention above with a trailing raw edge ID segment.
 */
export function floorPerimeterStableEdgeId(perimeterIndex: number, edgeId: string): string {
  return `outline:${perimeterIndex}:${edgeId}`;
}

export function floorOpeningStableEdgeId(openingIndex: number, edgeId: string): string {
  return `opening:${openingIndex}:${edgeId}`;
}

interface ParsedFloorStableEdgeId {
  readonly ring: "outline" | "opening";
  readonly index: number;
  readonly edgeId: string;
}

/** Parses a namespaced FLOOR `EdgeModifier.edgeId` produced by the helpers above. */
export function parseFloorStableEdgeId(stableEdgeId: string): ParsedFloorStableEdgeId | null {
  const match = /^(outline|opening):(\d+):(.*)$/.exec(stableEdgeId);
  if (!match) {
    return null;
  }
  const [, ring, indexStr, edgeId] = match;
  return { ring: ring as "outline" | "opening", index: Number(indexStr), edgeId };
}
